import { Link } from 'react-router-dom';
import { useRef, useCallback, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import projectsData from '../data/projectsData';

gsap.registerPlugin(ScrollTrigger);

function ProjectCard({ project, index }) {
  return (
    <article
      className="card proj-card"
      style={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: '360px',
        maxWidth: '360px',
        flexShrink: 0,
      }}
    >
      {/* Image */}
      <div style={{ height: '240px', overflow: 'hidden', flexShrink: 0 }}>
        <img
          src={project.image}
          alt={project.title}
          style={{
            width: '100%', height: '100%', objectFit: 'cover', display: 'block',
            filter: 'grayscale(30%)',
            transition: 'transform 0.6s cubic-bezier(0.16,1,0.3,1), filter 0.4s ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.07)'; e.currentTarget.style.filter = 'grayscale(0%)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.filter = 'grayscale(30%)'; }}
        />
      </div>

      {/* Content */}
      <div style={{ padding: '1.75rem', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        <div style={{ marginBottom: '0.85rem' }}>
          <span className="label-gold">Project {String(index + 1).padStart(2, '0')}</span>
        </div>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', marginBottom: '0.75rem', color: 'var(--white)', lineHeight: 1.2 }}>
          <Link to={project.details} style={{ color: 'inherit', textDecoration: 'none' }}>{project.title}</Link>
        </h3>
        <p style={{ color: 'var(--muted)', fontSize: '0.9rem', lineHeight: 1.7, marginBottom: '1.5rem', flexGrow: 1 }}>
          {project.summary}
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1.5rem' }}>
          {project.tech.map(t => <span key={t} className="tag">{t}</span>)}
        </div>

        <hr className="hr" style={{ marginBottom: '1.25rem' }} />
        <div style={{ display: 'flex', gap: '1.5rem' }}>
          <Link to={project.details} className="inline-link" style={{ fontSize: '0.85rem' }}>Case Study →</Link>
          <a href={project.github} target="_blank" rel="noopener noreferrer" className="inline-link" style={{ fontSize: '0.85rem' }}>GitHub →</a>
          {project.demo !== '#' && (
            <a href={project.demo} target="_blank" rel="noopener noreferrer" className="inline-link" style={{ fontSize: '0.85rem' }}>Live →</a>
          )}
        </div>
      </div>
    </article>
  );
}

export default function Projects() {
  const sectionRef = useRef();
  const trackRef = useRef();
  const scrollTarget = useRef(0);
  const isHovered = useRef(false);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartScroll = useRef(0);
  const [cursor, setCursor] = useState('grab');

  useGSAP(() => {
    gsap.fromTo(
      sectionRef.current,
      { opacity: 0, y: 40 },
      {
        opacity: 1, y: 0, duration: 0.8, ease: 'power3.out',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 85%',
          once: true,
        },
      }
    );
  }, { scope: sectionRef });

  /* Wheel → horizontal scroll (only when hovered, only when there is room) */
  const handleWheel = useCallback((e) => {
    if (!isHovered.current) return;

    const track = trackRef.current;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const atStart = scrollTarget.current <= 0;
    const atEnd   = scrollTarget.current >= maxScroll;

    // At left edge scrolling up → let page scroll up normally
    if (atStart && e.deltaY < 0) return;
    // At right edge scrolling down → let page scroll down normally
    if (atEnd && e.deltaY > 0) return;

    // In between → intercept and drive horizontal scroll
    e.preventDefault();
    scrollTarget.current = Math.max(0, Math.min(maxScroll, scrollTarget.current + e.deltaY * 1.2));

    gsap.to(track, {
      scrollLeft: scrollTarget.current,
      duration: 0.6,
      ease: 'power2.out',
      overwrite: true,
    });
  }, []);

  const handleMouseEnter = useCallback(() => {
    isHovered.current = true;
    window.addEventListener('wheel', handleWheel, { passive: false });
  }, [handleWheel]);

  const handleMouseLeave = useCallback(() => {
    isHovered.current = false;
    isDragging.current = false;
    setCursor('grab');
    window.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const handleMouseDown = useCallback((e) => {
    isDragging.current = true;
    dragStartX.current = e.clientX;
    dragStartScroll.current = scrollTarget.current;
    setCursor('grabbing');
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging.current) return;
    const track = trackRef.current;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const delta = dragStartX.current - e.clientX;
    scrollTarget.current = Math.max(0, Math.min(maxScroll, dragStartScroll.current + delta));
    gsap.to(track, {
      scrollLeft: scrollTarget.current,
      duration: 0.3,
      ease: 'power2.out',
      overwrite: true,
    });
  }, []);

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
    setCursor('grab');
  }, []);

  return (
    <section
      id="projects"
      ref={sectionRef}
      style={{ padding: '8rem 0', borderTop: '1px solid var(--border)', background: 'var(--bg)' }}
    >
      {/* Section header */}
      <div className="page-wrap">
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '3rem', flexWrap: 'wrap', gap: '2rem' }}>
          <div>
            <span className="sec-num">03</span>
            <h2 className="display-lg" style={{ marginTop: '-1.5rem' }}>Selected Work</h2>
          </div>
          <p style={{ maxWidth: '40ch', color: 'var(--muted)', fontSize: '1.05rem', lineHeight: 1.8 }}>
            Scroll to explore — each project is a story of craft and intent.
          </p>
        </div>
        <hr className="hr" style={{ marginBottom: '3rem' }} />
      </div>

      {/* Hover zone — intercepts wheel scroll */}
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        style={{ cursor, position: 'relative', userSelect: 'none' }}
      >
        {/* Scroll hint */}
        <p style={{
          textAlign: 'center',
          color: 'var(--muted)',
          fontSize: '0.72rem',
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          marginBottom: '1.25rem',
          opacity: 0.6,
        }}>
          ↕ Scroll · ← Drag →
        </p>

        {/* Card track — overflow hidden, GSAP drives scrollLeft */}
        <div
          ref={trackRef}
          style={{
            display: 'flex',
            gap: '2rem',
            overflowX: 'hidden',
            paddingLeft: 'calc((100vw - min(75rem, 100vw)) / 2 + 2rem)',
            paddingRight: 'calc((100vw - min(75rem, 100vw)) / 2 + 2rem)',
            paddingBottom: '2rem',
            alignItems: 'flex-start',
          }}
        >
          {projectsData.map((p, i) => (
            <ProjectCard key={p.id} project={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}